"use client";

import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { recordOpenHouseView, type OpenHouseViewSurface } from "@/lib/open-house-view";

/** A rendered row becomes a view only after half of it is visible for one second. */
export function OpenHouseViewRow({ as: Tag = "div", orgIdentifier, openHouseId, listingId, basePath, surface, className, style, children }: {
  as?: "div" | "li";
  orgIdentifier?: string;
  openHouseId: string;
  listingId: string;
  basePath: string;
  surface: OpenHouseViewSurface;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const element = useRef<HTMLElement | null>(null);
  const captured = useRef(false);
  useEffect(() => {
    if (!element.current || !orgIdentifier || typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let visible = false;
    const cancel = () => { clearTimeout(timer); timer = undefined; };
    const schedule = () => {
      cancel();
      if (!visible || document.visibilityState === "hidden" || captured.current) return;
      timer = setTimeout(() => {
        if (!visible || document.visibilityState === "hidden" || captured.current) return;
        captured.current = true;
        recordOpenHouseView({ orgIdentifier, openHouseId, listingId, basePath, surface,
          viewType: surface === "property_detail" ? "property_schedule_impression" : "list_impression" });
        observer.disconnect();
      }, 1000);
    };
    const observer = new IntersectionObserver((entries) => {
      const entry = entries.find((entry) => entry.target === element.current);
      if (!entry) return;
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      schedule();
    }, { threshold: 0.5 });
    observer.observe(element.current);
    document.addEventListener("visibilitychange", schedule);
    return () => { cancel(); observer.disconnect(); document.removeEventListener("visibilitychange", schedule); };
  }, [orgIdentifier, openHouseId, listingId, basePath, surface]);
  return <Tag ref={(node: HTMLElement | null) => { element.current = node; }} className={className} style={style}>{children}</Tag>;
}
